import {useId} from "react";
import {useTheme, type DefaultTheme} from "styled-components";
import {useLocale, useMessages} from "i18n";
import {useHasMounted} from "utils/useHasMounted";
import {ADVERTISE_PATH} from "sponsor/sponsorRoutes";
import {categoryGroups, templateAssetRange, type RenderedTemplateKey} from "sponsor/sponsorBooking";
import {ADVERTISED_TREATMENTS} from "./advertisedTreatments";
import {previewBackdropArt} from "sponsor/takeover/backdropArt";
import {SponsorTakeoverArt} from "sponsor/takeover/SponsorTakeover";
import {ADVERTISING_TREATMENT_ART_TESTID} from "sponsor/takeover/takeoverTestIds";
import {placeholderArtUrls, PreviewStage} from "sponsor/BackdropTemplatePicker.styles";
import {advertisingCopy} from "./copy/advertising";
import {SectionBand, SectionHead, SectionHeading, SectionLede} from "./AdvertisingContent.styles";
import {
	TreatmentArity,
	TreatmentBadge,
	TreatmentBody,
	TreatmentCard,
	TreatmentCategory,
	TreatmentCategoryLede,
	TreatmentCategoryName,
	TreatmentDescription,
	TreatmentGrid,
	TreatmentName,
	TreatmentStageFrame,
	TreatmentStagePlaceholder,
} from "./AdvertisingTreatments.styles";

/**
 * The treatment gallery on `/advertising` (ui#1861, widened to both categories in
 * kitecorp/transferify-ui#1870).
 *
 * Every card is generated from {@link ADVERTISED_TREATMENTS} — what a reader can both see and buy,
 * derived rather than listed — split into its two labelled categories by `categoryGroups`, the same
 * function both pickers group by. NOTHING about a treatment is written here:
 * the name and the description are `sponsor.templates[key]`, the category heading and its line are
 * `sponsor.categories[category]`, and the image count is `templateAssetRange` through the picker's own
 * `assetHint`. That is what stops this band becoming a second, stale description of the product — the
 * count in particular has been wrong twice (ui#624 added the coverflow while the prose still said
 * "three"; the wallpapers made "six" wrong the day they shipped), which is why the heading takes the
 * SAME figure the terms strip does rather than saying a number.
 *
 * The previews are the REAL renderer, mounted through the picker's own {@link PreviewStage} rather than
 * a lookalike stage, so what a buyer watches here is what the booking page shows them one click later
 * and what a recipient eventually sees. Each card links to that booking page: a reader who likes a look
 * should land where they can choose it.
 *
 * ## What is offered here, and what is not
 *
 * {@link ADVERTISED_TREATMENTS} rather than the raw catalogue, and that module states the two
 * conditions in full. A treatment whose creative is a real VIDEO would additionally need a caption
 * saying its preview is a stand-in still — a marketing page serves nobody's video — and that caption
 * belongs with the card, in whichever ticket makes such a treatment offerable again.
 *
 * ## Why they mount after hydration
 *
 * The same reason the hero's art does, once per card. `placeholderArtUrls` composes six SVG `data:`
 * URIs and each stage serialises them onto every tile it draws, so pre-rendering this band writes the
 * same plates into `build/advertising/index.html` dozens of times — on a public page whose HTML a CDN
 * serves to every crawler and every first paint. Deferring to {@link useHasMounted} keeps that HTML free
 * of them; {@link TreatmentStagePlaceholder} holds the stage's exact `16 / 10` box in the meantime, so
 * every card is already its final height and nothing below it moves when the art arrives.
 *
 * Each mount also stops animating when it scrolls out of view or the tab is backgrounded — decided
 * inside `SponsorTakeoverArt` by `useTakeoverPlayback`, per card, which is what makes ten of them on one
 * page affordable. Reduced motion is resolved there too, in the same place and by the same flag.
 */
export function AdvertisingTreatments() {
	const {locale} = useLocale();
	const {treatments} = advertisingCopy[locale];
	const m = useMessages().sponsor;
	const {tokens} = useTheme();
	const hasMounted = useHasMounted();
	const headingId = useId();

	return (
		<SectionBand aria-labelledby={headingId}>
			<SectionHead>
				{/* The figure the terms strip states, in the sentence that promises it. One read, so the
				    band cannot advertise a different number from the cards under it. */}
				<SectionHeading id={headingId}>
					{treatments.heading(ADVERTISED_TREATMENTS.length)}
				</SectionHeading>
				<SectionLede>{treatments.lede}</SectionLede>
			</SectionHead>
			{categoryGroups(ADVERTISED_TREATMENTS).map(({category, templates}) => (
				<TreatmentCategory key={category}>
					{/*
					  * The booking form's own heading and line for this category, not a second wording of
					  * them: a buyer who reads "Wallpaper" here and something else on the form has been
					  * shown two products. `categoryGroups` returns both groups whatever is in them, so an
					  * empty one would render a heading over nothing — which is the honest state, and
					  * unreachable while every catalogue entry has a category.
					  */}
					<TreatmentCategoryName>{m.categories[category].name}</TreatmentCategoryName>
					<TreatmentCategoryLede>{m.categories[category].lede}</TreatmentCategoryLede>
					<TreatmentGrid>
						{templates.map(template => {
							const range = templateAssetRange(template);
							const copy = m.templates[template];
							return (
								<TreatmentCard key={template} to={ADVERTISE_PATH}>
									<TreatmentStageFrame>
										{hasMounted
											? <TreatmentPreview template={template} tokens={tokens}/>
											: <TreatmentStagePlaceholder/>}
										{/*
										  * Gated with the art it labels. A badge reading "Live preview" over an empty box in
										  * the pre-rendered HTML would be the static page describing what it does not have.
										  */}
										{hasMounted && <TreatmentBadge>{m.templates.previewLabel}</TreatmentBadge>}
									</TreatmentStageFrame>
									<TreatmentBody>
										{/* Explicit space: the two sit at opposite ends of a flex row, but `textContent`
										    has no gaps of its own and "Carousel2-6 images" is what a screen reader
										    reads out of this heading without one. */}
										<TreatmentName>
											{copy.name}{" "}
											{/* A zero-image treatment has no arity to state; its creative is a video.
											    The picker withholds the hint on the same test. */}
											{range.max > 0 && (
												<TreatmentArity>{m.templates.assetHint(range.min, range.max)}</TreatmentArity>
											)}
										</TreatmentName>
										<TreatmentDescription>{copy.description}</TreatmentDescription>
									</TreatmentBody>
								</TreatmentCard>
							);
						})}
					</TreatmentGrid>
				</TreatmentCategory>
			))}
		</SectionBand>
	);
}

/**
 * One card's preview.
 *
 * A module-level component, never one declared inside {@link AdvertisingTreatments}: a nested
 * declaration is a NEW component type on every parent render, so React unmounts and remounts the whole
 * subtree — every animation restarting from frame zero each time anything above them re-rendered.
 *
 * `drive="sweep"` and `stage="tile"` are the picker's own two arguments, for the picker's own two
 * reasons: the sweep feeds every treatment from a self-playing clock (the hero treatment is scroll-linked
 * on a recipient page, and a 170px card gives it no scroll to read), and the tile stage selects the
 * showcase composition rather than the washed small-screen fallback.
 *
 * No video is offered, so this page fetches nobody's video and mounts no `<video>` at all — which
 * `advertising-entry.spec.ts` asserts as a count of zero.
 *
 * The testid is this page's own, not the renderer's default. The default is what
 * `advertising-entry.spec.ts` proves ABSENT here to show the marketing demo is not claiming the
 * recipient mount's identity — see `takeoverTestIds.ts`.
 */
function TreatmentPreview({template, tokens}: {template: RenderedTemplateKey; tokens: DefaultTheme["tokens"]}) {
	const art = previewBackdropArt(template, [], placeholderArtUrls(tokens));
	return (
		<PreviewStage>
			<SponsorTakeoverArt
				art={art}
				testId={ADVERTISING_TREATMENT_ART_TESTID}
				drive="sweep"
				stage="tile"
			/>
		</PreviewStage>
	);
}
